import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
} from '@mui/material';
import { memo, useEffect } from 'react';
import { CloseIcon } from '~/assets/icons';
import { useFormik } from 'formik';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { cast, handleApiError, notify } from '~/utils/helper';
import { useTranslation } from 'react-i18next';
import styles from './EditTermConditionDialog.module.scss';
import classNames from 'classnames/bind';
import { BaseRichEditor } from '~/components/common';
import { ITemplate, IEditTemplate } from '~/utils/interface/template';
import { useAppDispatch } from '~/redux/hooks';
import { handleCreateTemplate, handleUpdateTemplate } from '~/thunks/template/templateThunk';
import { templateActions } from '~/thunks/template/templateSlice';

const cx = classNames.bind(styles);

type EditTermConditionDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  mode?: string;
  data?: ITemplate;
};

const initialValues: IEditTemplate = {
  name: '',
  type: 'estimateTerm',
  content: '',
};

const EditEmailDialog = (props: EditTermConditionDialogProps) => {
  const { mode, data } = props;
  const dispatch = useAppDispatch();
  const { t } = useTranslation('admin/template');

  useEffect(() => {
    if (mode === DIALOG_MODE_EDIT) {
      form.setFieldValue('name', cast(data?.name));
      form.setFieldValue('content', cast(data?.content));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, data]);

  const handleCancel = () => {
    handleSetOpen(false);
  };
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      if (mode === DIALOG_MODE_CREATE) {
        try {
          await dispatch(handleCreateTemplate(values)).unwrap();
          notify('success', t('createSuccess', { ns: 'admin/common' }));
          dispatch(templateActions.changeNeedRefreshData(true));
          handleSetOpen(false);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
      if (mode === DIALOG_MODE_EDIT) {
        try {
          await dispatch(handleUpdateTemplate({ id: data?.id, type: data?.type, params: values })).unwrap();
          dispatch(templateActions.changeNeedRefreshData(true));
          notify('success', t('editSuccess', { ns: 'admin/common' }));
          handleSetOpen(false, true);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
    },
  });

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  const onChangeValue = (value: string) => {
    form.setFieldValue('content', value);
  };

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {mode === DIALOG_MODE_CREATE ? t('addTemplate') : t('editTemplate')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box className={`mb-[30px] ${cx('name')}`}>
          <InputLabel className='mb-[20px]'>{t('editEmailDialog.name')}</InputLabel>
          <TextField
            name='name'
            placeholder={t('editEmailDialog.name')}
            value={form.values.name}
            onChange={form.handleChange}
            error={Boolean(form.errors?.name)}
            helperText={form.errors?.name}
          />
        </Box>

        <Box className={cx('content')}>
          <InputLabel className='mb-[20px]'>{t('editEmailDialog.content')}</InputLabel>
          <BaseRichEditor
            onCreateNew={onChangeValue}
            valueProps={form.values.content}
            isError={Boolean(form.errors?.content)}
            errorMessage={form.errors.content}
          />
        </Box>
      </DialogContent>

      <DialogActions className='justify-end'>
        <Button color='secondary' variant='contained' onClick={() => handleCancel()}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button
          color='primary'
          variant='contained'
          onClick={() => {
            form.handleSubmit();
          }}
        >
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(EditEmailDialog);
